import {
  getTrustAccountAccessSection,
  getTrustAccountRowPermissions
} from './trust-account-access.utils';
import {getTrustAccountPath} from './trust-account-navigation.utils';

import type {
  TrustAccountAccessSection,
  TrustAccountRowPermissions
} from './trust-account-access.utils';
import type {TeamPerson} from '@/features/trust-accounts/members';
import type {GeneralSaleTransactionType, TrustPurpose, TrustStatus} from '@sollapay/enums';
import type {
  FirmTrustAccountListItemDTO,
  TrustAccountListLedgerDTO,
  TrustAccountListSellerDTO,
  TrustAccountListTeamMemberDTO
} from '@sollapay/types';

/** What every list surface (member or guest, table row or card) renders; built once per account. */
export interface TrustAccountListItem {
  id: string;
  refId: string;
  name: string;
  trustPurpose: TrustPurpose | null;
  generalSaleTransactionType: GeneralSaleTransactionType | null;
  trustStatus: TrustStatus;
  /** `undefined` while the balances are still loading; `null` when the account has none. */
  balance: string | null | undefined;
  date: Date | string;
  path: string;
}

export interface TrustAccountListTrustee extends TeamPerson {
  isCurrentUser: boolean;
}

export interface MemberTrustAccountListItem extends TrustAccountListItem {
  role: TrustAccountAccessSection;
  trustee: TrustAccountListTrustee;
  team: TrustAccountListTeamMemberDTO[];
  sellers: TrustAccountListSellerDTO[];
  buyersCount: number;
  permissions: TrustAccountRowPermissions;
  source: FirmTrustAccountListItemDTO;
}

/** What a member list row or card can do; `onAccountAction` serves both the CTA and the buyers count. */
export interface MemberTrustAccountListHandlers {
  onEditAccount?: (item: MemberTrustAccountListItem) => void;
  onDeleteAccount?: (item: MemberTrustAccountListItem) => void;
  onAccountAction?: (item: MemberTrustAccountListItem) => void;
  onManageTeam?: (item: MemberTrustAccountListItem) => void;
}

export function toMemberListItem(
  account: FirmTrustAccountListItemDTO,
  balancesMap?: Record<string, TrustAccountListLedgerDTO>
): MemberTrustAccountListItem {
  return {
    id: account.id,
    refId: account.refId,
    name: account.name,
    trustPurpose: account.trustPurpose ?? null,
    generalSaleTransactionType: account.generalSaleTransactionType ?? null,
    trustStatus: account.trustStatus,
    balance: balancesMap ? (balancesMap[account.id]?.balance ?? null) : undefined,
    date: account.createdAt,
    path: getTrustAccountPath(account),
    role: getTrustAccountAccessSection(account),
    trustee: {
      userId: account.lawyerId,
      name: account.lawyerName ?? undefined,
      picture: account.lawyerPicture ?? undefined,
      email: account.lawyerEmail ?? undefined,
      isCurrentUser: account.isTrustee
    },
    team: account.team,
    sellers: account.sellers,
    buyersCount: account.buyersCount,
    permissions: getTrustAccountRowPermissions(account),
    source: account
  };
}
